import React, { useMemo, useRef, useState } from 'react'
import { IconButton, Tooltip, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import FocusFadeGroup from 'components/FocusFadeGroup'
import ResponsiveDialog from 'components/ResponsiveDialog'
import AutoCompleteChipInput from 'components/AutoCompleteChipInput'
import InputGroup from 'components/InputGroup'
import { FormValue } from 'types'
import { Create } from '@mui/icons-material'
import SkillsInput from 'components/SkillsInput'
import SkillSuggestionsButton from 'components/SkillsInput/components/SkillSuggestionsButton'
import { graphql } from 'relay-runtime'
import { SkillSuggestionsForm } from 'components/SkillsInput/components/SkillSuggestionsButton/SkillSuggestionsForm'
import { useRefetchableFragment } from 'react-relay/hooks'
import { useLazyLoadQuery } from 'react-relay'
import { JobSkillsFields_SkillsQuery } from '__generated__/JobSkillsFields_SkillsQuery.graphql'
import { JobSkillsFields_SkillsFragment$key,
} from '__generated__/JobSkillsFields_SkillsFragment.graphql'
import JobSkillsCollection from 'components/JobSkillsCollection'
import { JobFormWrapper_Job$data } from '__generated__/JobFormWrapper_Job.graphql'
import useStyles from './styles'
import FormErrorHint from '../../FormErrorHint'

interface IJobSkillsFieldsProps {
  job_skills: FormValue<JobFormWrapper_Job$data['jobSkills']>
  industryId: FormValue<number>
  editable?: boolean
  requiredSkills?: boolean
  showError?: boolean
  label: string
}

export default function JobSkillsFields(props: IJobSkillsFieldsProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const [isEditorOpen, setEditorOpen] = useState(false)
  const [skillsFormOpened, setSkillsFormOpened] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const { job_skills, industryId, editable, requiredSkills = false, showError = true, label } = props

  const query = useLazyLoadQuery<JobSkillsFields_SkillsQuery>(graphql`
    query JobSkillsFields_SkillsQuery($editable: Boolean!) {
      ...JobSkillsFields_SkillsFragment
    }
  `, {
    editable: Boolean(editable),
  }, {
    fetchPolicy: 'store-and-network',
  })
  const [data, refetch] = useRefetchableFragment<JobSkillsFields_SkillsQuery, JobSkillsFields_SkillsFragment$key>(graphql`
    fragment JobSkillsFields_SkillsFragment on Query
    @refetchable(queryName: "JobSkillsField_SkillsQuery") {
      skills(first: 1000) @include(if: $editable) {
        edges {
          node {
            id
            name
            industries {
              rawId
            }
          }
        }
      }
    }
  `, query)
  const skills = useMemo(() => data?.skills?.edges?.map(e => e.node) || [], [data?.skills])

  let selectedIndustry = industryId.input.value
  let skillsFiltered = useMemo(() => (
    skills?.length > 0 && selectedIndustry ? (
      skills.filter(s => s.industries.map(i => i.rawId).indexOf(selectedIndustry) >= 0)
        .map(s => ({ id: s.id, name: s.name }))
    ) : []
  ), [skills, selectedIndustry])
  let selectedSkills = useMemo(() => (
    (job_skills.input.value || []).filter(skill => !!skill.required === requiredSkills) || []).map(s => ({
    id: s.id,
    skill_id: s.skill?.id,
    group_index: s.groupIndex,
    required_years: s.requiredYears,
    name: s.skill?.name,
    required: s.required,
  })), [job_skills.input.value, requiredSkills])

  const handleEditorOpen = () => setEditorOpen(true)

  const handleChange = (newSkills: any) => {
    let prevValue = job_skills.input.value || []

    if (requiredSkills) {
      newSkills.forEach((skill: any) => skill.required = true)
      job_skills.input.onChange(prevValue.filter(skill => !skill.required).concat(newSkills))
    } else {
      job_skills.input.onChange(prevValue.filter(skill => skill.required).concat(newSkills))
    }
  }

  const handleEditorClose = () => {
    job_skills.input.onBlur()

    setEditorOpen(false)
  }

  if (editable) {
    return (
      <div
        ref={container}
        className={classes.container}
        style={isEditorOpen ? { marginTop: theme.spacing(2) } : undefined}
      >
        <ResponsiveDialog open={isEditorOpen} onClose={handleEditorClose} maxWidth="md">
          <div style={{ width: 800 }} />

          <DialogTitle>
            {requiredSkills ? 'Edit must-have skills' : 'Edit nice-to-have skills'}
          </DialogTitle>

          <DialogContent>
            <div className={classes.inputWrapper} style={isEditorOpen ? undefined : { display: 'none' }}>
              <div style={{ marginBottom: theme.spacing(1) }}>
                {requiredSkills && (
                  <InputGroup>
                    <SkillsInput
                      input={{
                        ...job_skills.input,
                        value: selectedSkills,
                        onChange: handleChange,
                      }}
                      meta={job_skills.meta}
                      label="Must-have skills"
                      placeholder="Start typing skills..."
                      requiredSkills
                      fullWidth
                      hintText={(
                        <React.Fragment>
                          Can't find a skill you need? Simply{' '}
                          {/* for the case when button included form */}
                          {/* if responsive dialog appears in the hint it lose all the events */}
                          <SkillSuggestionsButton
                            onClick={() => setSkillsFormOpened(!skillsFormOpened)}
                          />{' '}to the list
                        </React.Fragment>
                      )}
                    />

                    <SkillSuggestionsForm
                      mode="job"
                      isOpened={skillsFormOpened}
                      onClose={() => {
                        setSkillsFormOpened(false)
                        refetch({
                          editable: Boolean(editable),
                        }, {
                          fetchPolicy: 'store-and-network',
                        })
                      }}
                    />
                  </InputGroup>
                )}

                {!requiredSkills && (
                  <AutoCompleteChipInput
                    label="Nice-to-have skills"
                    input={{
                      ...job_skills.input,
                      value: selectedSkills,
                      onChange: handleChange,
                    }}
                    placeholder="Start typing skills..."
                    suggestions={skillsFiltered}
                    suggestionsFormat={{ text: 'name', value: 'skill_id' }}
                    fullWidth
                  />
                )}
              </div>

              <FormErrorHint message={showError && job_skills.meta.touched && job_skills.meta.error} />
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleEditorClose} data-cy="job-job_skills-close">
              Close
            </Button>
          </DialogActions>
        </ResponsiveDialog>

        {skillsFiltered.length > 0 && (
          <FocusFadeGroup focused={isEditorOpen}>
            <div>
              {selectedSkills.length === 0 && (
                <span className={classes.placeholder}>
                  No {requiredSkills ? 'must have-skills selected (choose a max of three)' : 'nice-to-have skills selected'}
                </span>
              )}

              <div style={{ display: 'inline-block', maxWidth: 'calc(100% - 72px)' }}>
                <JobSkillsCollection skills={selectedSkills} />
              </div>

              <Tooltip
                title={requiredSkills ? 'Edit must have skills' : 'Edit nice to have skills'}
                placement="right"
              >
                <IconButton
                  onClick={handleEditorOpen}
                  className={classes.iconButton}
                  data-cy="job-job_skills-open"
                  size="large"
                >
                  <Create />
                </IconButton>
              </Tooltip>

              <FormErrorHint message={showError && job_skills.meta.touched && job_skills.meta.error} />
            </div>

            {(label && selectedSkills.length > 0) && (
              <Typography variant="body1" style={{ marginTop: theme.spacing(-1), marginBottom: theme.spacing(1) }}>
                {label}
              </Typography>
            )}
          </FocusFadeGroup>
        )}
      </div>
    )
  }

  // Non-editable version:

  if (selectedSkills.length === 0) {
    return null
  }

  return (
    <div ref={container}>
      <div>
        <JobSkillsCollection skills={selectedSkills} style={{ display: 'inline-block' }} />

        {(label && selectedSkills.length > 0) && (
          <Typography variant="body1" style={{ marginTop: theme.spacing(-1), marginBottom: theme.spacing(1) }}>
            {label}
          </Typography>
        )}
      </div>
    </div>
  )
}
